import { Area, AreaChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts'

import { useStoreAnalytics } from '@/hooks/features/analytics'
import { useBusiness } from '@/contexts/BusinessContext'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

import { ChartCard, ChartTooltip, chartAxis, chartGrid, compactNumber } from '@/components/dashboard/chart-kit'
import { StatCard } from '@/components/dashboard/StatCard'
import { trendOf } from '@/components/reports/useReportRange'

import { titleClass } from '../constants'
import { JourneyChart, ProductsTable, type AnalyticsFilters } from '../parts'
import { SignalsCard } from '../SignalsCard'

import type { AnalyticsOverview, AnalyticsProductRow } from '@/types/analytics'

/** The store at a glance: signals, the headline numbers, visits over time, the journey and the five most viewed products. */
export const OverviewTab = ({ o, range, onSeeProducts }: { o?: AnalyticsOverview; range: AnalyticsFilters; onSeeProducts: () => void }) => {
  const { formatCurrency } = useBusiness()
  const products = useStoreAnalytics<AnalyticsProductRow[]>('products', { ...range, sort: 'views', limit: 5 })
  const note = o ? `vs ${o.previous_range.from} – ${o.previous_range.to}` : undefined

  return (
    <div className="space-y-4">
      <SignalsCard />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {o ? (
          <>
            <StatCard title="Sessions" value={o.totals.sessions.toLocaleString()} {...trendOf(o.change.sessions)} note={note} spark={o.series.map((s) => s.sessions)} />
            <StatCard title="Visitors" value={o.totals.visitors.toLocaleString()} {...trendOf(o.change.visitors)} note={note} />
            <StatCard title="Conversion rate" value={`${o.totals.conversion_rate}%`} {...trendOf(o.change.conversion_rate)} note={note} spark={o.series.map((s) => s.conversion_rate)} />
            <StatCard title="Online orders" value={o.totals.orders.toLocaleString()} {...trendOf(o.change.orders)} note={`${formatCurrency(o.totals.order_value)} · avg ${formatCurrency(o.totals.average_order_value)}`} />
          </>
        ) : (
          Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-24" />)
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {o ? (
          <ChartCard
            title="Sessions over time"
            value={o.totals.sessions.toLocaleString()}
            subheader={`${o.range.from} – ${o.range.to} · ${o.range.timezone}`}
            empty={!o.series.some((s) => s.sessions)}
            emptyText="No visits in these dates."
            height={260}
            table={{ columns: ['Day', 'Sessions'], rows: o.series.map((s) => [s.date, s.sessions.toLocaleString()]) }}
          >
            <AreaChart data={o.series} margin={{ left: 0, right: 8 }}>
              <defs>
                <linearGradient id="analyticsSessionsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.16} />
                  <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...chartGrid} />
              <XAxis dataKey="date" {...chartAxis} minTickGap={24} />
              <YAxis {...chartAxis} tickFormatter={compactNumber} width={40} allowDecimals={false} />
              <Tooltip content={<ChartTooltip format={(v) => Number(v).toLocaleString()} />} />
              <Area type="monotone" dataKey="sessions" name="Sessions" stroke="hsl(var(--chart-1))" fill="url(#analyticsSessionsFill)" strokeWidth={2} />
            </AreaChart>
          </ChartCard>
        ) : (
          <Skeleton className="h-96" />
        )}
        <JourneyChart o={o} />
      </div>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <CardTitle className={titleClass}>Top products</CardTitle>
          <Button variant="secondary" size="sm" onClick={onSeeProducts}>
            See all products
          </Button>
        </CardHeader>
        <CardContent>
          <ProductsTable q={products} compact />
        </CardContent>
      </Card>
    </div>
  )
}
